<template>
	<div
		class="g-block-audio-player"
		:class="{
			'g-block-audio-player--edit': editable,
			'is-active': editable && active,
			'g-block-audio-player--h5': h5,
			'g-block-audio-player--card': blockStyle.showCard,
			'is-playing': isPlaying,
		}"
		:style="{ width: blockStyle.playerWidth }"
		@click="onWrapClick"
	>
		<div
			class="g-block-audio-player__shell"
			:style="shellStyle"
		>
			<img
				v-if="bgUrl"
				class="g-block-audio-player__bg"
				:src="bgUrl"
				alt=""
			/>
			<div
				v-if="blockStyle.darkOverlay"
				class="g-block-audio-player__overlay"
			></div>

			<div class="g-block-audio-player__content">
				<div class="g-block-audio-player__meta">
					<div
						class="g-block-audio-player__title"
						:style="{ color: blockStyle.titleColor }"
					>{{ normalizedData.title }}</div>
					<div
						class="g-block-audio-player__subtitle"
						:style="{ color: blockStyle.subtitleColor }"
					>{{ normalizedData.subtitle }}</div>
				</div>

				<div class="g-block-audio-player__progress-wrap">
					<div
						ref="progressTrack"
						class="g-block-audio-player__progress-track"
						:class="{ 'is-interactive': isInteractive }"
						:style="trackStyle"
						@click="onSeek"
						@mousedown.stop="onProgressMouseDown"
						@touchstart.stop.prevent="onProgressTouchStart"
					>
						<div
							class="g-block-audio-player__progress-bar"
							:style="progressBarStyle"
						></div>
						<div
							class="g-block-audio-player__progress-thumb"
							:style="thumbStyle"
						></div>
					</div>
					<div class="g-block-audio-player__time" :style="timeStyle">
						<span>{{ currentTimeText }}</span>
						<span>{{ durationText }}</span>
					</div>
				</div>

				<div class="g-block-audio-player__controls">
					<button
						type="button"
						class="g-block-audio-player__play"
						:style="iconStyle"
						:aria-label="isPlaying ? '暂停' : '播放'"
						@click.stop="togglePlay"
					>
						<span v-if="isPlaying" class="g-block-audio-player__pause"></span>
						<span v-else class="g-block-audio-player__triangle"></span>
					</button>
				</div>
			</div>
		</div>

		<audio
			v-if="permissionedAudioUrl || audioUrl"
			ref="audioEl"
			:src="permissionedAudioUrl || audioUrl"
			preload="metadata"
			@loadedmetadata="onLoadedMetadata"
			@timeupdate="onTimeUpdate"
			@ended="onEnded"
			@pause="onPause"
			@play="onPlay"
		></audio>
	</div>
</template>

<script>
	import baseFunc from '../../../../config/base_func.js';
	import { formatAudioDuration } from '../../../utils/localAudio.js';
	import {
		getAudioPlayerAudioUrl,
		getAudioPlayerCoverUrl,
		getAudioPlayerBlockStyle,
		normalizeAudioPlayerData,
	} from '../../../utils/audioPlayer.js';

	export default {
		name: 'GAudioPlayerShell',
		props: {
			data: {
				type: Object,
				required: true,
			},
			editable: {
				type: Boolean,
				default: false,
			},
			active: {
				type: Boolean,
				default: false,
			},
			h5: {
				type: Boolean,
				default: false,
			},
			idKey: {
				type: String,
				default: '',
			},
		},
		emits: ['select'],
		data() {
			return {
				isPlaying: false,
				currentTime: 0,
				duration: 0,
				permissionedAudioUrl: '',
				hasRequestedPermission: false,
				isDragging: false,
				draggingTime: 0,
			};
		},
		computed: {
			normalizedData() {
				return normalizeAudioPlayerData(this.data);
			},
			blockStyle() {
				return getAudioPlayerBlockStyle(this.data, { h5: this.h5 });
			},
			bgUrl() {
				return getAudioPlayerCoverUrl(this.data);
			},
			audioUrl() {
				return getAudioPlayerAudioUrl(this.data);
			},
			shellStyle() {
				return {
					borderRadius: this.blockStyle.borderRadius,
				};
			},
			trackStyle() {
				return {
					background: this.hexToRgba(this.blockStyle.themeColor, 0.35),
				};
			},
			progressBarStyle() {
				return {
					width: `${this.progressPercent}%`,
					background: this.blockStyle.themeColor,
				};
			},
			thumbStyle() {
				return {
					left: `${this.progressPercent}%`,
					background: this.blockStyle.themeColor,
				};
			},
			timeStyle() {
				return { color: this.blockStyle.themeColor };
			},
			iconStyle() {
				return { color: this.blockStyle.themeColor };
			},
			progressPercent() {
				if (!this.duration) return 0;
				if (this.isDragging) {
					return Math.min(100, (this.draggingTime / this.duration) * 100);
				}
				return Math.min(100, (this.currentTime / this.duration) * 100);
			},
			currentTimeText() {
				if (this.isDragging) return formatAudioDuration(this.draggingTime);
				return formatAudioDuration(this.currentTime);
			},
			durationText() {
				if (this.duration) {
					return formatAudioDuration(this.duration);
				}
				return this.normalizedData.audio?.durationText || '00:00';
			},
			isInteractive() {
				// 编辑画布与 H5 预览可播放；左侧缩略预览（editable/h5 均为 false）保持不可点
				return (this.editable || this.h5) && Boolean(this.audioUrl);
			},
		},
		watch: {
			data: {
				handler() {
					this.resetPlayer();
				},
				deep: true,
			},
		},
		mounted() {
			this.duration = this.normalizedData.audio?.duration || 0;
		},
		beforeUnmount() {
			this.pauseAudio();
			document.removeEventListener('mousemove', this._onGlobalMouseMove);
			document.removeEventListener('mouseup', this._onGlobalMouseUp);
			document.removeEventListener('touchmove', this._onGlobalTouchMove);
			document.removeEventListener('touchend', this._onGlobalTouchEnd);
		},
		methods: {
			hexToRgba(hex, alpha) {
				const normalized = String(hex || '#e84c3d').replace('#', '');
				const full = normalized.length === 3
					? normalized.split('').map(c => c + c).join('')
					: normalized;
				const num = parseInt(full, 16);
				if (Number.isNaN(num)) return `rgba(232, 76, 61, ${alpha})`;
				const r = (num >> 16) & 255;
				const g = (num >> 8) & 255;
				const b = num & 255;
				return `rgba(${r}, ${g}, ${b}, ${alpha})`;
			},
			onWrapClick(e) {
				if (!this.editable) return;
				e?.stopPropagation?.();
				this.$emit('select');
			},
			resetPlayer() {
				this.pauseAudio();
				this.currentTime = 0;
				this.duration = this.normalizedData.audio?.duration || 0;
			},
			onLoadedMetadata() {
				const audio = this.$refs.audioEl;
				if (audio && Number.isFinite(audio.duration)) {
					this.duration = Math.floor(audio.duration);
				}
			},
			onTimeUpdate() {
				const audio = this.$refs.audioEl;
				if (audio) {
					this.currentTime = audio.currentTime || 0;
				}
			},
			onPlay() {
				this.isPlaying = true;
			},
			onPause() {
				this.isPlaying = false;
			},
			onEnded() {
				this.isPlaying = false;
				this.currentTime = 0;
				const audio = this.$refs.audioEl;
				if (audio) {
					audio.currentTime = 0;
				}
			},
			togglePlay() {
				if (!this.isInteractive) return;
				const audio = this.$refs.audioEl;
				if (!audio) return;
				if (this.isPlaying) {
					audio.pause();
				} else {
					if (this.h5 && !this.hasRequestedPermission) {
						this.hasRequestedPermission = true;
						this.requestPlayPermission().then(() => {
							audio.play();
						}).catch(err => {
							this.$message.error(err.data?.message || '请求失败');
							this.hasRequestedPermission = false;
							audio.pause();
						});
					} else {
						audio.play();
					}
				}
			},
			//访问路径中取指定的值
			getQueryStringByChannel(name) {
				const reg = new RegExp('(^|&)' + name + '=([^&]*)(&|$)', 'i')
				if (!window.location.href.split('?')[1]) {
					return null
				}
				const re = window.location.href.split('?')[1].match(reg)
				if (re != null) return unescape(re[2])
				return null
			},
			requestPlayPermission() {
				return this.$apiRequest.post('/api/getMediaPlayPermissions', {
					attach_id: this.normalizedData.audio?.id,
					id_key: this.idKey,
					unique: '',
					app_type:1,
					p_key: this.getQueryStringByChannel('p_key')==null?'':this.getQueryStringByChannel('p_key'),
				}).then(res => {
					if (res.data?.play_url) {
						this.permissionedAudioUrl = baseFunc.getQiniusign(res.data.play_url);
					}
				});
			},
			pauseAudio() {
				const audio = this.$refs.audioEl;
				if (audio) {
					audio.pause();
				}
				this.isPlaying = false;
			},
			onSeek(e) {
				if (!this.isInteractive || !this.duration) return;
				const track = this.$refs.progressTrack;
				const audio = this.$refs.audioEl;
				if (!track || !audio) return;
				const rect = track.getBoundingClientRect();
				const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
				audio.currentTime = ratio * this.duration;
				this.currentTime = audio.currentTime;
			},
			onProgressMouseDown(event) {
				this._startDrag(event.clientX);
				document.addEventListener('mousemove', this._onGlobalMouseMove);
				document.addEventListener('mouseup', this._onGlobalMouseUp);
			},
			onProgressTouchStart(event) {
				const touch = event.touches?.[0];
				if (!touch) return;
				this._startDrag(touch.clientX);
				document.addEventListener('touchmove', this._onGlobalTouchMove, { passive: false });
				document.addEventListener('touchend', this._onGlobalTouchEnd);
			},
			_startDrag(clientX) {
				if (!this.isInteractive || !this.duration) return;
				this.isDragging = true;
				this.draggingTime = this.currentTime;
				this._updateDrag(clientX);
			},
			_onGlobalMouseMove(event) {
				this._updateDrag(event.clientX);
			},
			_onGlobalMouseUp() {
				this._endDrag();
				document.removeEventListener('mousemove', this._onGlobalMouseMove);
				document.removeEventListener('mouseup', this._onGlobalMouseUp);
			},
			_onGlobalTouchMove(event) {
				event.preventDefault();
				const touch = event.touches?.[0];
				if (!touch) return;
				this._updateDrag(touch.clientX);
			},
			_onGlobalTouchEnd() {
				this._endDrag();
				document.removeEventListener('touchmove', this._onGlobalTouchMove);
				document.removeEventListener('touchend', this._onGlobalTouchEnd);
			},
			_updateDrag(clientX) {
				if (!this.isDragging || !this.duration) return;
				const track = this.$refs.progressTrack;
				if (!track) return;
				const rect = track.getBoundingClientRect();
				if (!rect.width) return;
				const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
				this.draggingTime = ratio * this.duration;
			},
			_endDrag() {
				if (!this.isDragging) return;
				const targetTime = this.draggingTime;
				this.isDragging = false;
				this.currentTime = targetTime;
				const audio = this.$refs.audioEl;
				if (audio) {
					audio.currentTime = targetTime;
				}
			},
		},
	};
</script>

<style scoped lang="less">
	.g-block-audio-player {
		box-sizing: border-box;
		max-width: 100%;
		margin: 0 auto;

		&--edit {
			border: 1px solid transparent;
			border-radius: 4px;
			transition: border-color 0.2s, box-shadow 0.2s;
			cursor: pointer;

			&.is-active {
				border-color: #285af9;
				box-shadow: 0 0 0 1px rgba(40, 90, 249, 0.15);
			}
		}

		&--card {
			padding: 12px;
			background: #fff;
			box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
			border-radius: 8px;

			.g-block-audio-player--h5 & {
				padding: 0.24rem;
				border-radius: 0.16rem;
			}
		}
	}

	.g-block-audio-player__shell {
		position: relative;
		width: 100%;
		aspect-ratio: 2.35 / 1;
		overflow: hidden;
		background: #1a1a2e;
	}

	.g-block-audio-player__bg {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
		display: block;
	}

	.g-block-audio-player__overlay {
		position: absolute;
		inset: 0;
		background: rgba(0, 0, 0, 0.35);
		pointer-events: none;
	}

	.g-block-audio-player__content {
		position: relative;
		z-index: 1;
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		height: 100%;
		padding: 14px 16px 12px;
		box-sizing: border-box;

		.g-block-audio-player--h5 & {
			padding: 0.28rem 0.32rem 0.24rem;
		}
	}

	.g-block-audio-player__meta {
		min-width: 0;
	}

	.g-block-audio-player__title {
		font-size: 16px;
		font-weight: 700;
		line-height: 1.35;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;

		.g-block-audio-player--h5 & {
			font-size: 0.32rem;
		}
	}

	.g-block-audio-player__subtitle {
		margin-top: 10px;
		font-size: 12px;
		line-height: 1.4;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;

		.g-block-audio-player--h5 & {
			margin-top: 0.2rem;
			font-size: 0.24rem;
		}
	}

	.g-block-audio-player__progress-wrap {
		margin-top: auto;
		padding-top: 8px;

		.g-block-audio-player--h5 & {
			padding-top: 0.16rem;
		}
	}

	.g-block-audio-player__progress-track {
		position: relative;
		height: 3px;
		border-radius: 2px;
		overflow: visible;

		&.is-interactive {
			cursor: pointer;
		}

		.g-block-audio-player--h5 & {
			height: 0.06rem;
			border-radius: 0.04rem;
		}
	}

	.g-block-audio-player__progress-bar {
		position: absolute;
		left: 0;
		top: 0;
		height: 100%;
		border-radius: inherit;
		pointer-events: none;
	}

	.g-block-audio-player__progress-thumb {
		position: absolute;
		top: 50%;
		width: 10px;
		height: 10px;
		margin: -5px 0 0 -5px;
		border-radius: 50%;
		pointer-events: none;
		opacity: 0;
		transition: opacity 0.15s;

		.is-interactive & {
			opacity: 1;
		}

		.g-block-audio-player--h5 & {
			width: 0.2rem;
			height: 0.2rem;
			margin: -0.1rem 0 0 -0.1rem;
			opacity: 1;
		}
	}

	.g-block-audio-player__time {
		display: flex;
		justify-content: space-between;
		margin-top: 6px;
		font-size: 11px;
		line-height: 1;

		.g-block-audio-player--h5 & {
			margin-top: 0.12rem;
			font-size: 0.22rem;
		}
	}

	.g-block-audio-player__controls {
		position: relative;
		display: flex;
		align-items: center;
		justify-content: center;
		min-height: 28px;
		margin-top: 4px;

		.g-block-audio-player--h5 & {
			min-height: 0.56rem;
			margin-top: 0.08rem;
		}
	}

	.g-block-audio-player__play {
		border: none;
		background: transparent;
		padding: 0;
		cursor: default;
		line-height: 1;
		outline: none;

		.g-block-audio-player--edit &,
		.g-block-audio-player--h5 & {
			cursor: pointer;
		}
	}

	.g-block-audio-player__triangle {
		display: block;
		width: 0;
		height: 0;
		margin-left: 4px;
		border-top: 10px solid transparent;
		border-bottom: 10px solid transparent;
		border-left: 16px solid currentColor;

		.g-block-audio-player--h5 & {
			margin-left: 0.08rem;
			border-top-width: 0.2rem;
			border-bottom-width: 0.2rem;
			border-left-width: 0.32rem;
		}
	}

	.g-block-audio-player__pause {
		display: block;
		width: 14px;
		height: 16px;
		border-left: 4px solid currentColor;
		border-right: 4px solid currentColor;
		box-sizing: border-box;

		.g-block-audio-player--h5 & {
			width: 0.28rem;
			height: 0.32rem;
			border-left-width: 0.08rem;
			border-right-width: 0.08rem;
		}
	}
</style>
